import { Trans } from "@lingui/react/macro";

import { LegalPage } from "@/components/legal/legal-page";
import {
  getPhase4TrustDocument,
  getPhase6LegalMetadata,
  type Phase4TrustDocumentKey,
} from "@/integrations/multiresume/compliance/trust-copy";

type TrustDocumentPageProps = {
  documentKey: Phase4TrustDocumentKey;
};

export function TrustDocumentPage({ documentKey }: TrustDocumentPageProps) {
  const document = getPhase4TrustDocument(documentKey);
  const metadata = getPhase6LegalMetadata();

  return (
    <LegalPage title={document.title} description={document.description}>
      <section aria-label={metadata.versionLabel} data-legal-metadata="true">
        <h2>{metadata.versionLabel}</h2>
        <ul>
          <li>
            {metadata.controllerLabel}: {metadata.controller}
          </li>
          <li>
            {metadata.addressLabel}: {metadata.address}
          </li>
          <li>
            {metadata.effectiveDateLabel}: {metadata.effectiveDate}
          </li>
          <li>
            {metadata.versionLabel}: {metadata.version}
          </li>
          <li>
            {metadata.minimumAgeLabel}: {metadata.minimumAge}
          </li>
          <li>
            {metadata.supportLabel}: {metadata.support}
          </li>
          <li>
            {metadata.privacyLabel}: {metadata.privacy}
          </li>
        </ul>
      </section>

      {document.sections.map((section) => (
        <section key={section.title} id={section.id}>
          <h2 id={section.headingId}>{section.title}</h2>
          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {section.bullets && (
            <ul>
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          )}
          {section.links && (
            <p className="flex flex-wrap gap-3">
              {section.links.map((link) => (
                <a key={link.href} href={link.href}>
                  {link.label}
                </a>
              ))}
            </p>
          )}
        </section>
      ))}

      <ContextualTrustContact documentKey={documentKey} />
    </LegalPage>
  );
}

function ContextualTrustContact({ documentKey }: { documentKey: Phase4TrustDocumentKey }) {
  if (documentKey === "privacy") {
    return (
      <section data-phase6-state="privacy-rights">
        <h2>
          <Trans>Privacy requests</Trans>
        </h2>
        <p>
          <Trans>
            Contact our privacy team for a rights request. Do not include account credentials in your email.
          </Trans>
        </p>
        <a data-phase6-entry="privacy-contact" href="mailto:privacy@multiresu.me?subject=Privacy%20request">
          <Trans>Contact privacy</Trans>
        </a>
      </section>
    );
  }

  if (documentKey === "acceptableUsePolicy") {
    return (
      <section data-phase6-state="abuse-security">
        <h2>
          <Trans>Abuse and security reports</Trans>
        </h2>
        <p>
          <Trans>Report suspected abuse or a security concern with only the information needed to investigate.</Trans>
        </p>
        <a data-phase6-entry="abuse-contact" href="mailto:support@multiresu.me?subject=Abuse%20or%20security%20report">
          <Trans>Report abuse or security concern</Trans>
        </a>
      </section>
    );
  }

  return null;
}
